/**
 * Selettore dei parametri del diario di salute: la griglia a toggle divisa in
 * "soggettivi" / "oggettivi".
 *
 * È l'unico punto in cui quella griglia esiste: la usano la configurazione del
 * coach per il singolo atleta (`WellnessCoachDialog`), l'abilitazione massiva
 * (`WellnessBulkDialog`) e l'auto-attivazione dell'atleta (`WellnessDiary`).
 * Il catalogo arriva sempre dal backend (`GET /api/wellness/catalog`), qui non
 * se ne duplica nemmeno una voce.
 * Spec: docs/wellness/00-analisi-e-piano.md
 */
import { useMemo } from "react";
import { useLanguage } from "../../contexts/LanguageContext";

function FieldGroup({ title, fields, selected, onToggle, showHelp }) {
	if (!fields.length) return null;
	return (
		<section className="space-y-2">
			<p className="label-tactical">{title}</p>
			<div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
				{fields.map((field) => {
					const active = selected.includes(field.key);
					return (
						<button
							key={field.key}
							type="button"
							aria-pressed={active}
							onClick={() => onToggle(field.key)}
							className={`text-left rounded-sm border px-3 py-2 min-h-12 transition-colors ${
								active
									? "border-volt/60 bg-volt/10"
									: "border-border bg-secondary hover:border-volt/40"
							}`}
						>
							<span className="flex items-center justify-between gap-2 text-sm font-medium">
								{field.label}
								<span
									aria-hidden="true"
									className={`w-4 h-4 rounded-sm border flex items-center justify-center text-[10px] ${
										active ? "bg-volt border-volt text-white" : "border-border"
									}`}
								>
									{active ? "✓" : ""}
								</span>
							</span>
							{field.unit && (
								<span className="text-[10px] text-muted-foreground">
									{field.unit}
								</span>
							)}
							{showHelp && field.help && (
								<p className="text-xs text-muted-foreground mt-1">
									{field.help}
								</p>
							)}
						</button>
					);
				})}
			</div>
		</section>
	);
}

export function WellnessFieldPicker({
	catalog = [],
	selected = [],
	onToggle,
	showHelp = false,
}) {
	const { t } = useLanguage();
	const subjective = useMemo(
		() => catalog.filter((field) => field.category === "subjective"),
		[catalog],
	);
	const objective = useMemo(
		() => catalog.filter((field) => field.category === "objective"),
		[catalog],
	);

	return (
		<>
			<FieldGroup
				title={t("wellnessSubjectiveParameters")}
				fields={subjective}
				selected={selected}
				onToggle={onToggle}
				showHelp={showHelp}
			/>
			<FieldGroup
				title={t("wellnessObjectiveParameters")}
				fields={objective}
				selected={selected}
				onToggle={onToggle}
				showHelp={showHelp}
			/>
		</>
	);
}
